import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { ConfirmationProgressStatus } from '../../confirmation-progress.js';
import { Exam, type ExamState } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { Submission } from '../../submission.js';
import { checkGradingSchemeComplete, checkMasterSolutionComplete } from '../../utils/solution-completeness.js';
import { PendingTask, registerPendingTaskDescBuilder } from './pending-task.js';

export const SETUP_STEP_TASK_CLASS = 'SetupStepPendingTask';

/** Student-answer extraction result. */
export interface StudentAnswerResult {
  /** A re-extraction after the teacher re-uploaded submissions, vs. the initial extraction. */
  reupload: boolean;
  /** Extraction flagged low-confidence or error regions (mostly OCR failures, fixable by a quick
   *  rerun in the canvas) — the user should review them. */
  hasUncertainties: boolean;
  /** Submissions whose extraction failed entirely — display labels (student name where available).
   *  Nothing in the canvas can fix these; the teacher must re-upload them, so we only announce them. */
  erroredStudents: string[];
}

/** Master-solution / grading-scheme result. */
export interface SolutionResult {
  /** 'extraction' = read from an uploaded document, 'generation' = produced by the AI.
   *  Refinement/regeneration is intentionally absent: those run synchronously and report back to
   *  the agent directly, so they never go through this task. */
  type: 'extraction' | 'generation';
  reupload: boolean;
  /** Extraction flagged low-confidence/error regions. Always false for generation (no regions). */
  hasUncertainties: boolean;
  /** GS generation only: task paths (e.g. "2", "3.a") where the AI distributed the subtask points to
   *  a total different from the sheet's stated one — announced as a deliberate complexity-based
   *  adjustment, never as an arithmetic error. */
  pointAdjustments?: string[];
  /** UPLOAD (extraction) only: display task paths that are still missing required information after the
   *  upload — for the master solution a bounding box, for the grading scheme a bounding box and/or
   *  points. Computed against the DATABASE (checkMasterSolutionComplete / checkGradingSchemeComplete),
   *  filled in by updateSetupStep, and announced so the teacher knows what to add. Empty/absent when
   *  the upload is complete. */
  missingTaskPaths?: string[];
}

export interface SetupStepTaskOptions {
  studentAnswer: StudentAnswerResult | null;
  masterSolution: SolutionResult | null;
  gradingScheme: SolutionResult | null;
}

/**
 * What a finishing step reports. Each step sets only its own field; the others stay untouched so
 * concurrent steps accumulate into one task. `hasUncertainties` is NOT supplied by the caller —
 * updateSetupStep fills it in from the exam's extraction progress.
 */
export type SetupStepDelta = {
  studentAnswer?: Omit<StudentAnswerResult, 'hasUncertainties' | 'erroredStudents'>;
  masterSolution?: Omit<SolutionResult, 'hasUncertainties'>;
  gradingScheme?: Omit<SolutionResult, 'hasUncertainties'>;
};

// Registered entities mirroring the raw documents written by createSetupStepPendingTask. They give
// the polymorphic examState.pendingTasks array (and its nested result objects) constructors in the
// metaDataStore so hydrating the Exam entity (getByID / getEntityByID -> new ExamState) no longer
// throws "Unable to instantiate unknown entity SetupStepPendingTask". The raw push below is unchanged.
@Entity()
export class SetupStepStudentAnswerResult extends AdornisEntity {
  static override _class = 'SetupStepStudentAnswerResult';

  @Field(() => Boolean)
  reupload: Maybe<boolean>;

  @Field(() => Boolean)
  hasUncertainties: Maybe<boolean>;

  @Field(() => [String])
  erroredStudents: Maybe<string[]>;
}

@Entity()
export class SetupStepSolutionResult extends AdornisEntity {
  static override _class = 'SetupStepSolutionResult';

  @Field(() => String)
  type: Maybe<'extraction' | 'generation'>;

  @Field(() => Boolean)
  reupload: Maybe<boolean>;

  @Field(() => Boolean)
  hasUncertainties: Maybe<boolean>;

  @Field(() => [String])
  pointAdjustments: Maybe<string[]>;

  @Field(() => [String])
  missingTaskPaths: Maybe<string[]>;
}

@Entity()
export class SetupStepPendingTask extends PendingTask {
  static override _class = SETUP_STEP_TASK_CLASS;

  @Field(() => Boolean)
  isReadyForAgent: Maybe<boolean>;

  @Field(() => String)
  priority: Maybe<string>;

  @Field(() => SetupStepStudentAnswerResult)
  studentAnswer: Maybe<SetupStepStudentAnswerResult>;

  @Field(() => SetupStepSolutionResult)
  masterSolution: Maybe<SetupStepSolutionResult>;

  @Field(() => SetupStepSolutionResult)
  gradingScheme: Maybe<SetupStepSolutionResult>;
}

/**
 * Creates the raw document pushed to examState.pendingTasks. Fields not in the (enriched) delta get
 * their empty defaults so later steps can merge into them. `isReadyForAgent:false` holds the task
 * back from the agent until every setup step has finished.
 */
function createSetupStepPendingTask(enriched: Partial<SetupStepTaskOptions>): Record<string, unknown> {
  return {
    _class: SETUP_STEP_TASK_CLASS,
    isReadyForAgent: false,
    priority: 'workflow',
    studentAnswer: enriched.studentAnswer ?? null,
    masterSolution: enriched.masterSolution ?? null,
    gradingScheme: enriched.gradingScheme ?? null,
  };
}

/**
 * True if an extraction flagged low-confidence or error regions — both are reviewable/fixable in
 * the canvas (error regions are mostly OCR failures needing a quick rerun). Failed submissions
 * (errorSubmissions) are deliberately excluded: nothing in the canvas can fix them, so they're
 * announced separately (see resolveErroredStudentLabels / the desc builder).
 */
function hasExtractionUncertainties(progress: any): boolean {
  if (!progress) return false;
  const low = Array.isArray(progress.lowConfRegions) ? progress.lowConfRegions.length : 0;
  const errRegions = Array.isArray(progress.errorRegions) ? progress.errorRegions.length : 0;
  return low + errRegions > 0;
}

/**
 * Resolves failed answer-extraction submission IDs to display labels — student name where set,
 * otherwise a short fallback — so the agent can name the submissions the teacher must re-upload.
 */
async function resolveErroredStudentLabels(examId: string, submissionIds: string[]): Promise<string[]> {
  if (!submissionIds.length) return [];
  const submissionCollection = await getCollection(Submission);
  const submissions = await submissionCollection
    .find({ _id: { $in: submissionIds }, examId } as any, { projection: { studentName: 1 } } as any)
    .toArray();
  const nameById = new Map(
    submissions.map(s => [String((s as any)._id), String((s as any).studentName ?? '').trim()] as [string, string]),
  );
  return submissionIds.map(id => nameById.get(id) || `Abgabe ${id}`);
}

/**
 * True while any setup-related step is still running. Shared with `grading`'s own readiness gate (see
 * `GradingPayload.settle`) so "the exam is still working" means the same thing for both announcements.
 *
 * We define "running" as IN_PROGRESS or QUEUED: a step
 * that never runs for this exam stays NOT_STARTED forever (e.g. generation state when the master
 * solution was uploaded, not generated), so NOT_STARTED must not count as ongoing or the task would
 * never release. QUEUED is a step whose jobs are enqueued but waiting on a gate (answer extraction
 * waits for the structure confirmation) — it has been started, just not claimed yet.
 *
 * The split confirmation is part of the submission-upload pipeline (analyzing the split, or awaiting
 * the teacher's confirmation), so we hold the announcement back while it's active. Only STARTED/
 * RUNNING count: PENDING is the idle default (and means no split flow happened) and DONE/FAILED are
 * resolved — so this never blocks the non-submission flows (MS/GS generation), it just defers a
 * submission-related announcement until the split settles.
 */
export function anySetupStepOngoing(examState: ExamState): boolean {
  // QUEUED counts: an answer run waiting on the structure confirmation has been started as far as this gate cares.
  const stepRunning = [
    examState.answerExtractionProgress?.status,
    examState.masterSolutionProgress?.status,
    examState.gradingSchemeProgress?.status,
  ].some(status => status === StepState.IN_PROGRESS || status === StepState.QUEUED);

  const splitStatus = examState.splitConfirmationState?.status;
  const splitRunning =
    splitStatus === ConfirmationProgressStatus.STARTED || splitStatus === ConfirmationProgressStatus.RUNNING;

  return stepRunning || splitRunning;
}

/**
 * Called when one setup step (student-answer extraction, master-solution or grading-scheme
 * extraction/generation) finishes. Upserts the result into the exam's single SetupStep pending
 * task, then — once no setup step is still running — releases the task to the agent so it announces
 * all finished steps in one message instead of one per step.
 */
export async function updateSetupStep(examId: string, delta: SetupStepDelta): Promise<void> {
  if (!examId) return;
  // Nothing succeeded in this batch (e.g. every step failed) — don't create an empty task that
  // would render a bullet-less message to the agent.
  if (delta.studentAnswer == null && delta.masterSolution == null && delta.gradingScheme == null) return;

  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any);
  const examState = (exam as any)?.examState as ExamState | undefined;
  if (!examState) return;

  // Enrich each finishing step with its uncertainty flag, read from the matching extraction
  // progress (regions are populated during extraction, before the step is marked COMPLETED).
  // Generation produces no regions, so it never has uncertainties.
  const enriched: Partial<SetupStepTaskOptions> = {};
  if (delta.studentAnswer) {
    const answerProgress = examState.answerExtractionProgress as any;
    const erroredIds: string[] = Array.isArray(answerProgress?.errorSubmissions) ? answerProgress.errorSubmissions : [];
    enriched.studentAnswer = {
      ...delta.studentAnswer,
      hasUncertainties: hasExtractionUncertainties(answerProgress),
      erroredStudents: await resolveErroredStudentLabels(examId, erroredIds),
    };
  }
  if (delta.masterSolution) {
    // Only an UPLOAD (extraction) can be incomplete — generation guarantees coverage — so the DB-backed
    // completeness check only runs for that case; its missing task paths are announced below.
    const missingTaskPaths =
      delta.masterSolution.type === 'extraction' ? (await checkMasterSolutionComplete(examId)).missingTaskPaths : [];
    enriched.masterSolution = {
      ...delta.masterSolution,
      hasUncertainties:
        delta.masterSolution.type === 'extraction' && hasExtractionUncertainties(examState.masterSolutionProgress),
      missingTaskPaths,
    };
  }
  if (delta.gradingScheme) {
    const missingTaskPaths =
      delta.gradingScheme.type === 'extraction' ? (await checkGradingSchemeComplete(examId)).missingTaskPaths : [];
    enriched.gradingScheme = {
      ...delta.gradingScheme,
      hasUncertainties:
        delta.gradingScheme.type === 'extraction' && hasExtractionUncertainties(examState.gradingSchemeProgress),
      missingTaskPaths,
    };
  }

  // 1a. Create the task if it doesn't exist yet. The `$ne` guard makes this a no-op when it's
  //     already there, and MongoDB serializes writes per document, so two steps finishing at once
  //     can't double-create it — the loser's push simply matches nothing.
  await examCollection.updateOne(
    { _id: examId, 'examState.pendingTasks._class': { $ne: SETUP_STEP_TASK_CLASS } } as any,
    { $push: { 'examState.pendingTasks': createSetupStepPendingTask(enriched) } } as any,
  );

  // 1b. Merge this step's field(s) into the (now guaranteed) task. Only the fields present in the
  //     delta are set, so we never overwrite a sibling step's result with a default.
  const merge: Record<string, unknown> = {};
  if (enriched.studentAnswer !== undefined) merge['examState.pendingTasks.$.studentAnswer'] = enriched.studentAnswer;
  if (enriched.masterSolution !== undefined) merge['examState.pendingTasks.$.masterSolution'] = enriched.masterSolution;
  if (enriched.gradingScheme !== undefined) merge['examState.pendingTasks.$.gradingScheme'] = enriched.gradingScheme;
  if (Object.keys(merge).length) {
    await examCollection.updateOne(
      { _id: examId, 'examState.pendingTasks._class': SETUP_STEP_TASK_CLASS } as any,
      { $set: merge } as any,
    );
  }

  // 2. Hold the task back while any setup step is still running, so the agent fires once at the end.
  if (anySetupStepOngoing(examState)) {
    logger.info(`[SetupStep] ${examId}: setup steps still running — task held back from agent`);
    return;
  }

  // 3. Nothing left running — release the task. The $elemMatch guard flips it only on the
  //    false→true transition, so we trigger the agent exactly once even if several steps finish
  //    near-simultaneously (matchedCount is 0 once it's already true).
  const release = await examCollection.updateOne(
    {
      _id: examId,
      'examState.pendingTasks': { $elemMatch: { _class: SETUP_STEP_TASK_CLASS, isReadyForAgent: { $ne: true } } },
    } as any,
    { $set: { 'examState.pendingTasks.$.isReadyForAgent': true } } as any,
  );

  if (!release.matchedCount) return; // already released by a concurrent finish — don't re-trigger

  const conversationId = String((exam as any)?.conversationId ?? '').trim();
  if (!conversationId) {
    logger.warn(`[SetupStep] ${examId}: task released but no conversationId on exam — agent not triggered`);
    return;
  }

  // Flipping isReadyForAgent is itself an exam change, so a subscribed client's standing workflow
  // subscription picks the now-ready task up live; this fallback covers the no-client case.
  logger.info(`[SetupStep] ${examId}: all setup steps done — released task to agent`);
  // Resolved dynamically to avoid a static import cycle: workflow-agent → agent-loop →
  // exam-context → (registers this module's desc builder) → back here.
  const { scheduleWorkflowAgentFallback } = await import('./workflow-agent.js');
  scheduleWorkflowAgentFallback(conversationId, examId);
}

registerPendingTaskDescBuilder(SETUP_STEP_TASK_CLASS, task => {
  const t = task as unknown as SetupStepTaskOptions;
  const sa = t.studentAnswer;
  const ms = t.masterSolution;
  const gs = t.gradingScheme;
  const plural = +!!sa + +!!ms + +!!gs > 1;

  // --- bullets: which steps just finished ---
  let studentAnswerNote = '';
  if (sa) {
    studentAnswerNote = sa.reupload
      ? 'Die erneut hochgeladenen Schülerabgaben wurden vollständig erkannt.'
      : 'Die Schülerantworten wurden vollständig erkannt.';
  }
  let masterSolutionNote = '';
  if (ms) {
    masterSolutionNote =
      ms.type === 'generation'
        ? 'Die Musterlösung wurde vollständig generiert.'
        : ms.reupload
          ? 'Die erneut hochgeladene Musterlösung wurde vollständig erkannt.'
          : 'Die Erkennung der Musterlösung wurde vollständig abgeschlossen.';
  }
  let gradingSchemeNote = '';
  if (gs) {
    gradingSchemeNote =
      gs.type === 'generation'
        ? 'Das Bewertungsschema wurde vollständig generiert.'
        : gs.reupload
          ? 'Das erneut hochgeladene Bewertungsschema wurde vollständig erkannt.'
          : 'Die Erkennung des Bewertungsschemas wurde vollständig abgeschlossen.';
  }

  // --- canvas guidance ---
  // answers canvas ↔ student answers; msedit canvas ↔ master solution & grading scheme (shared).
  // OPEN directly on uncertainties or generated content; otherwise (clean extraction) only OFFER.
  // At most one canvas auto-opens — student answers take priority; the rest is offered/mentioned.
  const answersRelevant = !!sa;
  const answersOpen = !!sa?.hasUncertainties;
  const mseditRelevant = !!ms || !!gs;
  const mseditOpen =
    ms?.type === 'generation' || gs?.type === 'generation' || !!ms?.hasUncertainties || !!gs?.hasUncertainties;
  const generated = ms?.type === 'generation' || gs?.type === 'generation';

  const canvasLines: string[] = [];
  if (answersOpen) {
    canvasLines.push(
      'Bei den erkannten Schülerantworten gibt es unsichere oder fehlerhafte Stellen. Öffne deshalb direkt den ' +
        'Antworten-Canvas (Tool open_canvas mit canvas:"answers"), damit der Nutzer sie überprüfen und korrigieren kann.',
    );
    if (mseditOpen) {
      canvasLines.push(
        'Weise außerdem darauf hin, dass Musterlösung/Bewertungsschema im Canvas "msedit" überprüft werden können.',
      );
    } else if (mseditRelevant) {
      canvasLines.push('Biete zusätzlich an, Musterlösung/Bewertungsschema bei Bedarf im Canvas "msedit" zu öffnen.');
    }
  } else if (mseditOpen) {
    canvasLines.push(
      'Öffne direkt den Canvas "msedit" (Tool open_canvas mit canvas:"msedit"), damit der Nutzer ' +
        'Musterlösung/Bewertungsschema überprüfen kann.',
    );
    if (answersRelevant) {
      canvasLines.push(
        'Biete zusätzlich an, die erkannten Schülerantworten bei Bedarf im Antworten-Canvas ("answers") zu öffnen.',
      );
    }
  } else {
    // Nothing needs immediate review — just offer the relevant canvas(es).
    if (answersRelevant) {
      canvasLines.push(
        'Biete dem Nutzer an, die erkannten Schülerantworten im Antworten-Canvas ("answers") zu öffnen, falls er ' +
          'sie überprüfen möchte.',
      );
    }
    if (mseditRelevant) {
      canvasLines.push(
        'Biete dem Nutzer an, Musterlösung/Bewertungsschema im Canvas "msedit" zu öffnen, falls er sie überprüfen möchte.',
      );
    }
  }
  if (generated) {
    canvasLines.push(
      'Erkläre dem Nutzer außerdem, dass die generierte(n) Musterlösung bzw. das generierte Bewertungsschema jederzeit ' +
        'anhand seines Feedbacks angepasst werden können — er muss dir nur mitteilen, was geändert werden soll.',
    );
  }

  // Submissions that failed extraction entirely — announce and offer retry via the tool.
  let erroredNote = '';
  if (sa?.erroredStudents?.length) {
    erroredNote =
      `Wichtig: Folgende Schülerabgaben konnten nicht extrahiert werden: ${sa.erroredStudents.join(', ')}. ` +
      'Teile dies dem Nutzer ausdrücklich mit und biete ihm an, die Extraktion erneut zu versuchen ' +
      '(Tool reprocess_errored_submissions aufrufen — braucht keine Argumente). ' +
      'Falls auch der erneute Versuch scheitert, muss der Nutzer diese Abgaben neu hochladen.';
  }

  const informNote =
    'Deine Aufgabe: Teile dem Nutzer in deiner Antwort zuerst klar und ausdrücklich mit, welche dieser Schritte ' +
    'soeben abgeschlossen wurden. Diese Mitteilung steht im Vordergrund und darf nicht durch eine reine Tool-Aktion ' +
    'ersetzt werden.';

  // Incomplete UPLOAD: tasks for which the uploaded master solution / grading scheme is still missing
  // required information (a master-solution region; a grading-scheme region and/or points). Announce so
  // the teacher can add them — grading is blocked until the upload is complete.
  const missingNotes: string[] = [];
  if (ms?.missingTaskPaths?.length) {
    const taskList = ms.missingTaskPaths.map(path => `Aufgabe ${path}`).join(', ');
    missingNotes.push(
      `Wichtig: Für folgende Aufgaben wurde keine Musterlösung erkannt: ` +
        `${taskList}. Teile dem Nutzer ausdrücklich mit, dass die Musterlösung dort noch unvollständig ist und im ` +
        `Canvas ergänzt werden muss, bevor bewertet werden kann.`,
    );
  }
  if (gs?.missingTaskPaths?.length) {
    const taskList = gs.missingTaskPaths.map(path => `Aufgabe ${path}`).join(', ');
    missingNotes.push(
      `Wichtig: Für folgende Aufgaben wurde kein vollständiges Bewertungsschema erkannt: ` +
        `${taskList}. Teile dem Nutzer ausdrücklich mit, dass das Bewertungsschema dort noch unvollständig ist und im ` +
        `Canvas ergänzt werden muss, bevor bewertet werden kann.`,
    );
  }

  // Point differences. For GENERATION: the AI's distribution differs from the sheet's stated total — frame it
  // as a deliberate, considered choice (never a correction/mismatch). For EXTRACTION: the uploaded grading
  // scheme stated different points than the confirmed structure — frame it neutrally as a found discrepancy
  // to review. Both point the user to the canvas.
  let pointAdjustmentNote = '';
  if (gs?.type === 'generation' && gs.pointAdjustments?.length) {
    const taskList = gs.pointAdjustments.map(path => `Aufgabe ${path}`).join(', ');
    pointAdjustmentNote =
      `Erwähne außerdem beiläufig und selbstbewusst, dass du die Punkte bei folgenden Aufgaben verteilt hast, um die ` +
      `relative Komplexität der Teilaufgaben zueinander besser abzubilden: ${taskList}. Formuliere es als bewusste ` +
      `Entscheidung (z. B. „Ich habe die Punkte bei diesen Aufgaben angepasst, damit sie die relative Komplexität ` +
      `besser widerspiegeln.“), niemals als Korrektur, Abweichung oder Rechenfehler. Weise darauf hin, dass er die ` +
      `Punkte im Bewertungsschema-Canvas jederzeit selbst prüfen und anpassen kann.`;
  } else if (gs?.type === 'extraction' && gs.pointAdjustments?.length) {
    const taskList = gs.pointAdjustments.map(path => `Aufgabe ${path}`).join(', ');
    pointAdjustmentNote =
      `Weise den Nutzer außerdem darauf hin, dass du für folgende Aufgaben die Punkte aus dem hochgeladenen ` +
      `Bewertungsschema übernommen hast, die von der bestätigten Aufgabenstruktur abweichen: ${taskList}. Formuliere ` +
      `es sachlich als Hinweis (keine Wertung, kein Fehler) und bitte ihn, die Punkte im Canvas zu prüfen ` +
      `und bei Bedarf anzupassen.`;
  }

  return (
    `### WORKFLOW TASK: ${plural ? 'Folgende Schritte wurden abgeschlossen:' : 'Folgender Schritt wurde abgeschlossen:'}` +
    (studentAnswerNote ? `\n- ${studentAnswerNote}` : '') +
    (masterSolutionNote ? `\n- ${masterSolutionNote}` : '') +
    (gradingSchemeNote ? `\n- ${gradingSchemeNote}` : '') +
    `\n\n${informNote}` +
    (missingNotes.length ? `\n${missingNotes.join('\n')}` : '') +
    (pointAdjustmentNote ? `\n${pointAdjustmentNote}` : '') +
    (erroredNote ? `\n${erroredNote}` : '') +
    (canvasLines.length ? `\n${canvasLines.join(' ')}` : '')
  );
});
